import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Cpu } from "lucide-react";
import {
  fetchAdminDevices,
  fetchAdminOrgs,
  assignDevice,
  type AdminDevice,
} from "@/api/admin";

export function AdminDevices() {
  const qc = useQueryClient();
  const devicesQ = useQuery({ queryKey: ["admin-devices"], queryFn: fetchAdminDevices });
  const orgsQ = useQuery({ queryKey: ["admin-orgs"], queryFn: fetchAdminOrgs });

  const [msg, setMsg] = useState<string | null>(null);

  const registeredOrgs = (orgsQ.data?.orgs ?? []).filter((o) => o.is_registered);

  const assign = useMutation({
    mutationFn: ({ id, orgId }: { id: string; orgId: string | null }) =>
      assignDevice(id, orgId),
    onSuccess: () => {
      setMsg("Device assignment updated.");
      qc.invalidateQueries({ queryKey: ["admin-devices"] });
    },
    onError: (e: unknown) => setMsg(`Assign failed: ${String(e)}`),
  });

  const devices = devicesQ.data?.devices ?? [];
  const orgName = (id: string | null) =>
    id ? registeredOrgs.find((o) => o.id === id)?.name ?? id : "";

  return (
    <div className="p-lg space-y-md">
      <header>
        <h1 className="text-xl font-semibold text-text flex items-center gap-sm">
          <Cpu size={20} className="text-brand" /> Devices
        </h1>
        <p className="text-sm text-textLight">
          Devices with kraken access (discovered from ait-auth). Assign each to a
          registered organization.
        </p>
      </header>

      {msg && (
        <div className="px-md py-sm rounded-md text-sm border bg-info-bg text-info-text border-info-text/20">
          {msg}
        </div>
      )}

      <div className="rounded-lg border border-border bg-surface shadow-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg text-textLight text-left">
            <tr>
              <th className="px-md py-sm font-medium">Serial</th>
              <th className="px-md py-sm font-medium">Model</th>
              <th className="px-md py-sm font-medium">Setup</th>
              <th className="px-md py-sm font-medium">Last seen</th>
              <th className="px-md py-sm font-medium">Organization</th>
            </tr>
          </thead>
          <tbody>
            {devicesQ.isLoading && (
              <tr><td colSpan={5} className="px-md py-lg text-center text-textLight">Loading…</td></tr>
            )}
            {!devicesQ.isLoading && devices.length === 0 && (
              <tr><td colSpan={5} className="px-md py-lg text-center text-textLight">
                No devices discovered. Run a sync from the Organizations page.
              </td></tr>
            )}
            {devices.map((d: AdminDevice) => (
              <tr key={d.talis_auth_id} className="border-t border-border">
                <td className="px-md py-sm text-text font-medium">{d.serial_number}</td>
                <td className="px-md py-sm text-textLight">{d.model}</td>
                <td className="px-md py-sm text-textLight">{d.is_setup ? "Yes" : "Pending"}</td>
                <td className="px-md py-sm text-textLight">
                  {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : "Never"}
                </td>
                <td className="px-md py-sm">
                  <select
                    value={d.org_id ?? ""}
                    onChange={(e) =>
                      assign.mutate({ id: d.talis_auth_id, orgId: e.target.value || null })
                    }
                    className="px-sm py-xs rounded-md border border-border text-sm bg-surface cursor-pointer"
                    title={orgName(d.org_id)}
                  >
                    <option value="">— Unassigned —</option>
                    {registeredOrgs.map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminDevices;
